import React, { useState } from 'react';
import { MotionImage, MotionButton } from '../ui/MotionPrimitives';

const ServicesOverview: React.FC = () => {
  return (
    <section className="w-full bg-white py-16">
      <div className="mx-auto flex max-w-6xl flex-col gap-12 px-6 md:px-10 lg:flex-row lg:items-stretch">
        {/* Image column */}
        <div className="w-full overflow-hidden rounded-2xl bg-gray-100 lg:w-5/12">
          <div className="h-full w-full overflow-hidden">
            <MotionImage
              src="/images/pics/services.jpg"
              alt="Premium real estate developments"
              className="h-full w-full object-cover object-center"
            />
          </div>
        </div>

        {/* Text column */}
        <div className="w-full lg:w-7/12">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black">
            Comprehensive Service Portfolio
          </p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-black md:text-4xl">
            Strategic support across the full real estate value chain
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-black md:text-base">
            Our service offerings are structured to cover the entire real estate value chain,
            ensuring comprehensive support for both end-users and sophisticated investors.
          </p>

          <div className="mt-10 divide-y divide-gray-200 border border-gray-200 rounded-2xl bg-white">
            {/* Section A */}
            <AccordionSection
              id="A"
              title="A. Investment & Development Advisory"
              openLabel="Hide details"
              closedLabel="View details"
            >
              <div className="space-y-3 text-sm leading-relaxed text-black md:text-base">
                <div>
                  <span className="font-semibold">Strategic Investment Consultation:</span>{' '}
                  <span>
                    Personalized guidance on portfolio construction, risk management, and market
                    timing across our dual-market platform.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Property Development and Construction Projects:</span>{' '}
                  <span>
                    Consultation and partnership on new construction and redevelopment projects,
                    from residential communities to commercial complexes.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Specialized Sector Investment:</span>{' '}
                  <span>
                    Expert advisory and brokerage services in high-value, niche property sectors,
                    including Healthcare (Hospitals) facilities.
                  </span>
                </div>
              </div>
            </AccordionSection>

            {/* Section B */}
            <AccordionSection
              id="B"
              title="B. Property Transactions (Sales and Acquisitions)"
              openLabel="Hide details"
              closedLabel="View details"
            >
              <div className="space-y-3 text-sm leading-relaxed text-black md:text-base">
                <p>
                  We provide comprehensive sales and acquisition services across all asset
                  classes, ensuring maximum value extraction for clients.
                </p>
                <div>
                  <span className="font-semibold">Off-Plan Properties and New Developments:</span>{' '}
                  <span>
                    Exclusive access to pre-construction and new development opportunities,
                    securing favorable pricing and maximizing potential capital gains.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Secondary Market (Resale Properties):</span>{' '}
                  <span>
                    Expert brokerage for existing residential and commercial assets, including
                    Apartments, Villas, Plots, and Buildings.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Land and Plot Sales:</span>{' '}
                  <span>
                    Facilitation of transactions for prime Residential and Commercial plots, vital
                    for large-scale development or long-term land banking strategies.
                  </span>
                </div>
              </div>
            </AccordionSection>

            {/* Section C */}
            <AccordionSection
              id="C"
              title="C. Property Management, Leasing, and Hotel Sales"
              openLabel="Hide details"
              closedLabel="View details"
            >
              <div className="space-y-3 text-sm leading-relaxed text-black md:text-base">
                <p>
                  Our dedicated division provides full-service property management and specialized
                  sales in the hospitality sector.
                </p>
                <div>
                  <span className="font-semibold">Property Leasing:</span>{' '}
                  <span>
                    Comprehensive services for securing long-term and short-term tenancy
                    agreements for Residential and Commercial Rentals.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Hotel Sales:</span>{' '}
                  <span>
                    Specialized brokerage and advisory for the acquisition and disposition of
                    hotel properties (aligned with Hospitality sector expertise).
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Tenant Sourcing and Management:</span>{' '}
                  <span>
                    Vetting, placement, and professional management of tenants to ensure property
                    preservation and consistent cash flow.
                  </span>
                </div>
              </div>
            </AccordionSection>

            {/* Section D */}
            <AccordionSection
              id="D"
              title="D. Investor Concierge Services"
              openLabel="Hide details"
              closedLabel="View details"
            >
              <div className="space-y-3 text-sm leading-relaxed text-black md:text-base">
                <div>
                  <span className="font-semibold">UAE Golden Visa Services:</span>{' '}
                  <span>
                    We offer specialized Golden Visa services tailored for real estate owners,
                    featuring fast-track processing typically completed within 2-3 working days.
                    We ensure a hassle-free, VIP experience for clients, guaranteeing dedicated,
                    24/7 support throughout the application process.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Partnership Program for Golden Visa:</span>{' '}
                  <span>
                    We actively collaborate with real estate companies and agents, offering a
                    generous commission share for client referrals.
                  </span>
                </div>
                <div>
                  <span className="font-semibold">Why Partner with Us?</span>{' '}
                  <span>
                    We guarantee quick and efficient visa processing, dedicated support, and VIP
                    service for your referred clients, enhancing your reputation and service
                    offering.
                  </span>
                </div>
              </div>
            </AccordionSection>
          </div>

          <p className="mt-8 text-sm font-semibold uppercase tracking-[0.18em] text-black">
            Strategic Real Estate Global Impact
          </p>
        </div>
      </div>
    </section>
  );
};

interface AccordionSectionProps {
  id: string;
  title: string;
  openLabel: string;
  closedLabel: string;
  children: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  id,
  title,
  openLabel,
  closedLabel,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <MotionButton
        type="button"
        className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-semibold text-black md:text-base"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={`services-accordion-${id}`}
      >
        <span>{title}</span>
        <span className="ml-4 text-xs font-medium uppercase tracking-[0.18em] text-[rgb(216,133,70)]">
          {isOpen ? openLabel : closedLabel}
        </span>
      </MotionButton>
      {isOpen && (
        <div
          id={`services-accordion-${id}`}
          className="px-5 pb-5 pt-1"
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default ServicesOverview;
